"use client";

import { MouseEvent, useCallback, useState, ButtonHTMLAttributes } from "react";
import { captureException } from "@sentry/nextjs";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import useQuery from "@/app/src/hooks/useQuery";
import { ApiStatus, PropertyDataV2 } from "commonModule/services/common";
import { AddFavouritesListResponseData, AddFavouritesListResponseDataData } from "commonModule/services/favorite/createFavoriteListByPropertyId";
import LoaderDots from "components/icons-svg/LoaderDots";
import { useIsAgent } from "hooks/useIsAgent";
import { markAuthStore, markSoftLoginState, selectIsUserLoggedIn, selectIsUserSoftLoggedIn, selectUser } from "stores/auth/authSlice";
import { SoftLoginModalType } from "stores/auth/types";
import { selectBookingStore, selectPdpFees, selectTotalExcludingTaxes, selectTotalIncludingTaxes } from "stores/booking/bookingSlice";
import { addToFavoriteListByPropertyId, removeFromFavoriteListThunk } from "stores/favorites/favoritesSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { createUpdatePriceDropAlert, selectPriceDropId, selectSwitchPriceDrop, setSwitchPriceDrop } from "stores/price-drop/priceDropSlice";
import { selectSearchQuery } from "stores/search/searchSlice";
import useCustomToast from "utils/hooks/useCustomToast";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { getPropertyTypeDesc } from "utils/property-utils";
const WatchlistListModel = dynamic(() => import( /* webpackChunkName: "WatchlistListModel" */"components/pages/vacation-rentals/watchlist-list-model"), {
  ssr: false
});
type Favbtn = {
  callbackWhenTriggeredWithoutLogin?: () => void;
  children: ({
    ...injectedProps
  }: ButtonHTMLAttributes<HTMLButtonElement>) => JSX.Element;
  city: string;
  country: string;
  favListName?: string;
  favoriteListId?: string;
  isFavorited: boolean;
  numberOfBedroom: number;
  propertyId: string;
  propertyName: string;
  propertyTypeDesc: string;
  state: string;
  setIsCloseOnOutsideEnabled?: (value: boolean) => void;
  property?: PropertyDataV2;
  loaderDotsWidth?: number;
  loaderDotsHeight?: number;
};
const FavoriteButtonForPropertyPopOverVersion = ({
  favoriteListId,
  favListName,
  isFavorited,
  propertyId,
  propertyName,
  children,
  city,
  state,
  country,
  numberOfBedroom,
  propertyTypeDesc,
  setIsCloseOnOutsideEnabled,
  callbackWhenTriggeredWithoutLogin = () => {},
  property,
  loaderDotsHeight = 20,
  loaderDotsWidth = 20
}: Favbtn) => {
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const isUserSoftLoggedIn = useAppSelector(selectIsUserSoftLoggedIn);
  const bookingStore = useAppSelector(selectBookingStore);
  const switchPriceDrop = useAppSelector(selectSwitchPriceDrop);
  const isPriceDropId = useAppSelector(selectPriceDropId);
  const totalExcludingTaxesWhimstay = useAppSelector(selectTotalExcludingTaxes);
  const totalIncludingTaxesWhimstay = useAppSelector(selectTotalIncludingTaxes);
  const searchQuery = useAppSelector(selectSearchQuery);
  const pdpFees = useAppSelector(selectPdpFees);
  const dispatch = useAppDispatch();
  const {
    customToast
  } = useCustomToast();
  const pathname = usePathname();
  const isDetailsPage = pathname?.startsWith("/detail");
  const isAgent = useIsAgent();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFavFromAPI, setSelectedFavFromAPI] = useState<{
    favouriteListId: number | string;
    favouriteListName: string;
  } | null>(null);
  const {
    updateQuery
  } = useQuery();
  const {
    isOpen: isFavouritesModalOpen,
    onToggle: onFavouritesModalToggle,
    onClose: onFavouritesModalClose
  } = useDisclosure();
  const desc = getPropertyTypeDesc(propertyTypeDesc || "", city || "", state || "", country || "", property?.postalName);
  const handleCloseFavoritesModal = () => {
    onFavouritesModalClose();

    // this is for property card displayed on map when clicked on pins
    // we added functionality to close modal on outside click
    // so below we are enabling it after favorites modal is closed
    // it gets disabled when favorite button is clicked.
    setIsCloseOnOutsideEnabled?.(true);
  };
  const priceDropAlertError = (errorMessage?: string) => {
    void (async () => {
      const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
      mixpanelV2Client.CLICK_PRICE_DROP_WATCH_ERROR({
        type: "Watch Property",
        propertyId: bookingStore?.propertyId || "",
        term: null,
        datesSelected: `${bookingStore?.startDate}, ${bookingStore?.endDate}`,
        numOfGuests: bookingStore?.adultNumber + bookingStore?.childNumber,
        pageOrigin: "property detail page",
        totalDisplayPriceExcludingTaxes: totalExcludingTaxesWhimstay,
        errorMessage: errorMessage || ""
      });
    })();
  };
  const getPriceDropAlertPayload = (isPriceDropAlertEnabled: boolean) => {
    const searchPagePayload = {
      checkInDate: property?.checkInDate || searchQuery?.startDate || "",
      checkOutDate: property?.checkOutDate || searchQuery?.endDate || "",
      propertyId: propertyId || null,
      adults: searchQuery?.adultCount || 2,
      children: searchQuery?.childrenCount || 0,
      petsAllowed: searchQuery?.petAllowed || false,
      propertyPrice: property?.totalBeforeTaxes || null,
      priceDropAlertId: !isPriceDropAlertEnabled ? isPriceDropId : null,
      locationName: null,
      noOfNights: property?.noOfNights || searchQuery?.noOfDays || 0,
      searchRequestData: null,
      isPriceDropAlertEnabled // set to false to disable price drop alerts
    };
    const pdpPagePayload = {
      checkInDate: bookingStore?.startDate || "",
      checkOutDate: bookingStore?.endDate || "",
      propertyId: bookingStore?.propertyId || null,
      adults: bookingStore?.adultNumber || 0,
      children: bookingStore?.childNumber || 0,
      petsAllowed: bookingStore?.pet || false,
      petNumber: bookingStore?.petNumber || 0,
      propertyPrice: Number((totalIncludingTaxesWhimstay + pdpFees)?.toFixed(2)) || null,
      priceDropAlertId: !isPriceDropAlertEnabled ? isPriceDropId : null,
      locationName: null,
      noOfNights: bookingStore?.dayCount || 0,
      searchRequestData: null,
      isPriceDropAlertEnabled
    };
    return isDetailsPage ? pdpPagePayload : searchPagePayload;
  };
  const showSuccessToast = (isPriceDropAlertEnabled: boolean, data?: AddFavouritesListResponseDataData) => {
    customToast({
      type: "success",
      title: "Saved to a collection!",
      linkName: "Go to your collections",
      linkURL: "/favorites",
      message: `This ${desc} was ${isPriceDropAlertEnabled ? "saved" : "unsaved"} to the ${(data?.count || 0) > 1 ? data?.name : ""} collection`,
      duration: 8000,
      className: "!bottom-[unset] !left-[unset] !right-4 !top-[62px] md:z-[999]"
    });
  };

  // API request to update the status of price drop alerts for the current booking.
  const updatePriceDropAlertSwitch = (isPriceDropAlertEnabled: boolean, data?: AddFavouritesListResponseDataData) => {
    void dispatch(createUpdatePriceDropAlert({
      data: getPriceDropAlertPayload(isPriceDropAlertEnabled)
    })).unwrap().then(response => {
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        if (isPriceDropAlertEnabled) {
          mixpanelV2Client?.CLICK_PRICE_DROP_WATCH_CREATED({
            type: "Watch Property",
            propertyId: bookingStore?.propertyId || "",
            term: null,
            datesSelected: `${bookingStore?.startDate}, ${bookingStore?.endDate}`,
            numOfGuests: bookingStore?.adultNumber + bookingStore?.childNumber,
            pageOrigin: "property detail page",
            totalDisplayPriceExcludingTaxes: totalExcludingTaxesWhimstay || 0
          });
        } else {
          mixpanelV2Client.CLICK_PRICE_DROP_WATCH_REMOVED({
            type: "Watch Property",
            propertyId: bookingStore?.propertyId || "",
            term: null,
            datesSelected: `${bookingStore?.startDate}, ${bookingStore?.endDate}`,
            numOfGuests: bookingStore?.adultNumber + bookingStore?.childNumber,
            pageOrigin: "property detail page",
            totalDisplayPriceExcludingTaxes: null,
            currentPrice: totalExcludingTaxesWhimstay
          });
        }
      })();
      if (response?.apiStatus === "SUCCESS") {
        if (isDetailsPage) {
          dispatch(setSwitchPriceDrop(isPriceDropAlertEnabled));
        }
        showSuccessToast(isPriceDropAlertEnabled, data);
      } else {
        if (isDetailsPage) {
          dispatch(setSwitchPriceDrop(!isPriceDropAlertEnabled));
        }
        // show toast message when PDA is already on and user click on favorite icon
        if (response?.errorMessage === "Price drop alert already exists.") {
          showSuccessToast(isPriceDropAlertEnabled, data);
        }
        void priceDropAlertError(response?.errorMessage);
      }
      return true;
    }).catch((err: TypeError) => {
      dispatch(setSwitchPriceDrop(!isPriceDropAlertEnabled));
      customToast({
        type: "error",
        message: err?.message || "Something went wrong",
        duration: 2000,
        className: "!bottom-[unset] !left-[unset] !right-4 !top-[62px]"
      });
      captureException(new Error(`Error 'priceDropAlertApi' ${JSON.stringify(err)}`));
      void priceDropAlertError(err?.message);
    });
  };
  const setPDPAlertAutomatically = (data: AddFavouritesListResponseDataData) => {
    void (async () => {
      const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
      mixpanelV2Client.CLICK_PRICE_DROP_WATCH({
        type: "Watch Property",
        propertyId: bookingStore?.propertyId || "",
        term: null,
        datesSelected: `${bookingStore?.startDate}, ${bookingStore?.endDate}`,
        numOfGuests: bookingStore?.adultNumber + bookingStore?.childNumber,
        pageOrigin: "property detail page",
        totalDisplayPriceExcludingTaxes: totalExcludingTaxesWhimstay || 0
      });
    })();
    // Trigger signup modal if user not logged in
    if (!isLoggedIn && !isUserSoftLoggedIn) {
      dispatch(markSoftLoginState({
        softLoginType: SoftLoginModalType.PDA_SOFT_LOGIN,
        softLoginPayload: {
          emailId: "",
          propertyId: bookingStore?.propertyId || "",
          createFavouriteList: false,
          messageToHost: false,
          userPriceDropAlertRequest: {
            checkInDate: bookingStore?.startDate || "",
            checkOutDate: bookingStore?.endDate || "",
            propertyId: bookingStore?.propertyId,
            adults: bookingStore?.adultNumber || 0,
            children: bookingStore?.childNumber || 0,
            petsAllowed: bookingStore?.pet || false,
            petNumber: bookingStore?.petNumber || 0,
            propertyPrice: Number((totalIncludingTaxesWhimstay + pdpFees)?.toFixed(2)) || 0,
            isPriceDropAlertEnabled: true,
            locationName: null,
            priceDropAlertId: null,
            noOfNights: bookingStore?.dayCount || 0,
            searchRequestData: null
          }
        },
        softSignupType: SoftLoginModalType.PDA_SOFT_LOGIN,
        softSignupTitle: "You're all set,",
        softSignupDescription: propertyTypeDesc ? `We'll notify you when the price for this <b>${propertyTypeDesc} in ${city}, ${state}</b> goes down.` : "",
        softSignupSubTitle: "Secure your alert and get notifications faster on your phone",
        softSignupCheckboxText: "Change your message preferences here",
        actionToPerformAfterSoftLogin() {
          // If the user clicks the switch without logging in and is prompted to soft login,
          // then after a success
          // update the price drop alert state
          dispatch(setSwitchPriceDrop(true));
        }
      }));
      dispatch(markAuthStore({
        ui: {
          actionToPerformAfterLoginOrSignup: () => {
            // If the user clicks the switch without logging in and is prompted to soft login,
            // then after a successful response if user is existing then
            // the Price Drop Alert API is automatically called to create the alert for the user.
            void updatePriceDropAlertSwitch(true, data);
          }
        }
      }));
      return;
    } else if (!switchPriceDrop) {
      updatePriceDropAlertSwitch(true, data); // Call API to enable price drop alert
    }
    // else {
    //   dispatch(setSwitchPriceDrop(!switchPriceDrop)) // Toggle switch UI state
    //   updatePriceDropAlertSwitch(false) // Call API to disable price drop alert
    // }
  };
  const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setTimeout(() => {
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client.CLICKED_SAVE_PROPERTY({
          propertyId,
          city,
          state,
          numberOfBedroom,
          propertyTypeDesc
        });
      })();
    }, 0);
    if (!isLoggedIn && !isUserSoftLoggedIn) {
      dispatch(markSoftLoginState({
        softLoginType: SoftLoginModalType.FAV_SOFT_LOGIN,
        softLoginPayload: {
          propertyId,
          createFavouriteList: true,
          emailId: ""
        },
        softSignupType: SoftLoginModalType.FAV_SOFT_LOGIN,
        softSignupTitle: "Saved!",
        softSignupDescription: propertyTypeDesc ? `This property is now on your watchlist!` : "",
        softSignupSubTitle: "Secure your alert and get notifications faster on your phone",
        softSignupCheckboxText: "Change your message preferences here",
        setPDAAPIsPayloadData: {
          data: getPriceDropAlertPayload(true),
          desc
        }
      }));
      dispatch(markAuthStore({
        dialCode: null,
        mobileNumber: null,
        countryCode: null,
        errorCode: null,
        error: null,
        ui: {
          actionToPerformAfterLoginOrSignup: () => {
            // onFavouritesModalToggle()
            void dispatch(addToFavoriteListByPropertyId({
              params: {
                propertyId: propertyId
              }
            })).unwrap().then((response: AddFavouritesListResponseData) => {
              setSelectedFavFromAPI({
                favouriteListId: response.data.favouriteId,
                favouriteListName: response.data.name
              });

              // When a user clicks the favorite icon, automatically enable price drop alerts.
              // Do not trigger price drop alerts if the current user role is a "travel agent"
              if (!isAgent && !switchPriceDrop) {
                updatePriceDropAlertSwitch(true, response.data); // Call API to enable price drop alert
              }
              return true;
            });
          }
        }
      }));
      callbackWhenTriggeredWithoutLogin?.();
      return;
    }
    if (!isFavorited && (isLoggedIn || isUserSoftLoggedIn)) {
      // onFavouritesModalToggle()
      void dispatch(addToFavoriteListByPropertyId({
        params: {
          propertyId: propertyId
        }
      })).unwrap().then((response: AddFavouritesListResponseData) => {
        setSelectedFavFromAPI({
          favouriteListId: response.data.favouriteId,
          favouriteListName: response.data.name
        });

        // Show success toast in two cases:
        // 1. Price drop alert is already enabled and user clicks favorite icon
        // 2. If the current user role is a "travel agent" and user clicks favorite icon (no PDA involved)
        if (switchPriceDrop || isAgent && response?.apiStatus === ApiStatus.SUCCESS) {
          onFavouritesModalToggle();
          // showSuccessToast(true,response.data)
        }

        // When a user clicks the favorite icon, automatically enable price drop alerts.
        // Do not trigger price drop alerts if the current user role is a "travel agent"
        if (!isAgent) {
          setPDPAlertAutomatically(response.data);
        }
        return true;
      });
    }
    if (isFavorited) {
      setIsLoading(true);
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        mixpanelV1Client.SAVE_TO_FAVOURITES({
          userName: user?.firstName,
          city,
          state,
          country,
          isFavourite: false,
          isFavouriteList: true,
          favouriteListName: favListName,
          favouriteListId: favoriteListId
        });
      })();
      void dispatch(removeFromFavoriteListThunk({
        params: {
          propertyId: propertyId,
          favoriteId: "0",
          favourite: false
        }
      })).unwrap().then(response => {
        if (response?.apiStatus === "SUCCESS") {
          customToast({
            type: "success",
            title: "Favorite removed",
            message: `${desc} is no longer marked as a favorite`,
            duration: 8000,
            className: "!bottom-[unset] !left-[unset] !right-4 !top-[62px]"
          });
        }
        setIsLoading(false);
        updateQuery({
          refetch: Date.now()
        }); // This will update the search query and new data will be loaded
        return;
      });
    }
  }, [isAgent, getPriceDropAlertPayload, desc, callbackWhenTriggeredWithoutLogin, city, country, dispatch, favListName, favoriteListId, isFavorited, isLoggedIn, numberOfBedroom, propertyId, propertyTypeDesc, state, updateQuery, setPDPAlertAutomatically, switchPriceDrop, updatePriceDropAlertSwitch, user?.firstName, showSuccessToast]);
  return <>
      {isLoading ? <div className='min-w-auto flex items-center justify-center rounded-full bg-[#000a] p-1'>
          <LoaderDots height={loaderDotsHeight} width={loaderDotsWidth} />
        </div> : <>
          {children && children({
        role: "button",
        "aria-label": "Options",
        onClick: handleClick,
        className: "p-0 rounded-full"
      })}
        </>}

      {isFavouritesModalOpen && <WatchlistListModel propertyName={propertyName} city={city} state={state} country={country} propertyID={propertyId} onClose={handleCloseFavoritesModal} preSelectedFav={selectedFavFromAPI} property={property} />}
    </>;
};
export default FavoriteButtonForPropertyPopOverVersion;